import "@/App.css";
import { useState, useEffect, useRef } from "react";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Star, 
  Clock, 
  Shield, 
  CheckCircle, 
  Wrench,
  Droplets,
  Flame,
  ShowerHead,
  AlertCircle,
  Home,
  PenTool,
  Menu,
  X,
  ChevronRight,
  Quote,
  Award,
  Users,
  ThumbsUp,
  Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

// Business Data
const BUSINESS = {
  name: "Richard Knight Plumbing",
  phone: "0404 012 233",
  email: "contact@richardknightplumbing.com.au",
  location: "Newtown, Inner West Sydney",
  rating: 5.0,
  totalReviews: 68,
  yearsExperience: "20+"
};

// Services Data
const SERVICES = [
  {
    id: 1,
    slug: "blocked-drains",
    title: "Blocked Drains",
    description: "Fast, effective drain clearing for sinks, showers, toilets and stormwater drains. We diagnose the problem properly before recommending solutions.",
    icon: Droplets,
    featured: true
  },
  {
    id: 2,
    slug: "hot-water-systems",
    title: "Hot Water Systems",
    description: "Supply, installation and repairs for all types of hot water systems. Honest advice on repair versus replacement.",
    icon: Flame,
    featured: true
  },
  {
    id: 3,
    slug: "leaking-taps",
    title: "Leaking Taps & Toilets",
    description: "Stop wasting water and money with prompt repairs to dripping taps and running toilets.",
    icon: Wrench,
    featured: false
  },
  {
    id: 4,
    slug: "gas-fitting",
    title: "Gas Fitting",
    description: "Licensed gas fitter for installations, repairs and safety checks. All work compliant with Australian standards.",
    icon: Flame,
    featured: false
  },
  {
    id: 5,
    slug: "emergency-plumbing",
    title: "Emergency Plumbing",
    description: "When plumbing problems cannot wait, Richard responds quickly to urgent call-outs across the Inner West.",
    icon: AlertCircle,
    featured: true
  },
  {
    id: 6,
    slug: "bathroom-renovations",
    title: "Bathroom Renovations",
    description: "Quality plumbing work for your bathroom renovation. Working alongside builders and directly with homeowners.",
    icon: ShowerHead,
    featured: false
  },
  {
    id: 7,
    slug: "general-plumbing",
    title: "General Plumbing Repairs",
    description: "From burst pipes to general maintenance, reliable repairs done right the first time.",
    icon: PenTool,
    featured: false
  },
  {
    id: 8,
    slug: "roof-gutters",
    title: "Roof & Gutter Leaks",
    description: "Diagnosis and repair of roof plumbing issues including leaking gutters and downpipes.",
    icon: Home,
    featured: false
  }
];

// Testimonials Data
const TESTIMONIALS = [
  {
    id: 1,
    quote: "We have relied on Richard for over a decade. Always reliable, clear in his communication, and fairly priced. Would not trust anyone else.",
    source: "Homeowner, Newtown"
  },
  {
    id: 2,
    quote: "Prompt response to an emergency on a weekend. Arrived quickly, fixed the problem efficiently, and cleaned up afterwards. Excellent service.",
    source: "Local Resident, Marrickville"
  },
  {
    id: 3,
    quote: "Finally found a plumber who gives honest advice. He told us what actually needed fixing rather than trying to sell unnecessary work.",
    source: "Homeowner, Enmore"
  },
  {
    id: 4,
    quote: "Used Richard for multiple renovation projects. His work is always clean and precise. Highly recommend to anyone needing a reliable tradesman.",
    source: "Local Builder, Inner West"
  },
  {
    id: 5,
    quote: "After years of trying different plumbers, we finally found someone we can trust. Richard is now our go-to for all plumbing needs.",
    source: "Long-term Customer, Stanmore"
  },
  {
    id: 6,
    quote: "Clear communication from start to finish. He explained everything, provided options, and never pressured us. Fair pricing too.",
    source: "Homeowner, Petersham"
  }
];

// Service Areas
const SERVICE_AREAS = [
  "Newtown", "Marrickville", "Enmore", "Stanmore", 
  "Petersham", "Leichhardt", "Annandale", "Camperdown",
  "Erskineville", "St Peters", "Dulwich Hill", "Ashfield"
];

// Why Choose Us Data
const WHY_CHOOSE = [
  {
    icon: Clock,
    title: "20+ Years Experience",
    description: "Two decades of serving Inner West families and businesses with reliable plumbing solutions."
  },
  {
    icon: ThumbsUp,
    title: "Honest Advice",
    description: "Straightforward recommendations based on what you actually need, not what makes the most profit."
  },
  {
    icon: Award,
    title: "Clean Workmanship",
    description: "Tidy work sites, quality finishes, and respect for your home every time."
  },
  {
    icon: Users,
    title: "Repeat Customers",
    description: "Many customers have trusted Richard for 10 to 20 years. That loyalty speaks for itself."
  },
  {
    icon: Shield,
    title: "Licensed & Insured",
    description: "Fully licensed plumber and gas fitter with comprehensive insurance coverage."
  },
  {
    icon: Heart,
    title: "Community Focused",
    description: "A local plumber who genuinely cares about the Inner West community he serves."
  }
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#why-us", label: "Why Us" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header shadow-md" : "bg-white"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-1" data-testid="logo-link">
            <img
              // src="https://customer-assets.emergentagent.com/job_3d9d93ab-e25a-4850-bb7a-ddafb0558447/artifacts/akpu30p9_ChatGPT%20Image%20Feb%206%2C%202026%2C%2002_03_43%20PM.png"
              src="/logo.png"
              alt="Richard Knight Plumbing"
              className="h-14 w-auto"
            />
            <span className="company-name text-trust-navy font-semibold text-xl md:text-2xl">
              Richard Knight Plumbing
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                className="text-slate-600 hover:text-trust-navy font-medium transition-colors link-underline"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <a 
              href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
              className="hidden sm:flex items-center gap-2 bg-reliable-sky hover:bg-reliable-sky-dark text-white rounded-full px-6 py-3 font-semibold transition-all btn-primary"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" />
              <span>{BUSINESS.phone}</span>
            </a>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-trust-navy"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 animate-fade-in" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a 
                  key={link.href}
                  href={link.href} 
                  className="text-slate-600 hover:text-trust-navy font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a 
                href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-center gap-2 bg-reliable-sky text-white rounded-full px-6 py-3 font-semibold mt-2"
              >
                <Phone className="w-4 h-4" />
                <span>{BUSINESS.phone}</span>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-accent-bg rounded-full px-4 py-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-trust-navy">5.0 Rating from 68 Reviews</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-trust-navy leading-tight tracking-tight">
              Your Trusted Local Plumber in{" "}
              <span className="text-reliable-sky">Newtown</span> & the Inner West
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              Reliable, honest plumbing services from an experienced local tradesman. 
              Quality workmanship and clear communication, every time.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center gap-2 bg-reliable-sky hover:bg-reliable-sky-dark text-white rounded-full px-8 py-4 text-lg font-semibold shadow-lg btn-primary"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a 
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-trust-navy border-2 border-trust-navy rounded-full px-8 py-4 text-lg font-semibold btn-secondary"
                data-testid="hero-quote-btn"
              >
                Request a Quote
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>

            {/* Quick Trust Points */}
            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="w-5 h-5 text-reliable-sky" />
                <span className="text-sm font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="w-5 h-5 text-reliable-sky" />
                <span className="text-sm font-medium">20+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="w-5 h-5 text-reliable-sky" />
                <span className="text-sm font-medium">Same-Day Service</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative lg:h-[600px] animate-fade-in delay-200">
            <div className="relative h-full">
              <div className="hero-image-mask h-full rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  // src="https://images.unsplash.com/photo-1661044437616-71100c9f638e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwbHVtYmVyJTIwd29ya2luZyUyMHVuZGVyJTIwc2lua3xlbnwwfHx8fDE3NzAzNDE3MDl8MA&ixlib=rb-4.1.0&q=85"
                  src="/bathroom.png"
                  alt="Professional plumber at work"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-2xl shadow-xl p-4 badge-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-trust-navy text-xl">5.0</p>
                    <p className="text-xs text-slate-500">Google Rating</p>
                  </div>
                </div>
              </div>

              {/* Experience Badge */}
              <div className="absolute -right-4 bottom-1/4 bg-trust-navy text-white rounded-2xl shadow-xl p-4 badge-float delay-500">
                <div className="text-center">
                  <p className="font-heading font-bold text-3xl">20+</p>
                  <p className="text-xs text-slate-300">Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Signals Section
const TrustSignals = () => {
  const signals = [
    { icon: Star, label: "5.0 Star Rating", sublabel: "68 Google Reviews" },
    { icon: Clock, label: "20+ Years", sublabel: "Local Experience" },
    { icon: MapPin, label: "Inner West", sublabel: "Sydney Focus" },
    { icon: Shield, label: "Licensed", sublabel: "& Fully Insured" }
  ];

  return (
    <section className="py-12 bg-accent-bg border-y border-slate-100" data-testid="trust-signals">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {signals.map((signal, index) => (
            <div 
              key={index}
              className="flex items-center gap-4 justify-center md:justify-start"
            >
              <div className="trust-icon-container">
                <signal.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-heading font-bold text-trust-navy">{signal.label}</p>
                <p className="text-sm text-slate-500">{signal.sublabel}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [highlighted, setHighlighted] = useState(null);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#service-')) {
        const slug = hash.replace('#service-', '');
        setHighlighted(slug);
        setTimeout(() => setHighlighted(null), 2000);
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  return (
    <section id="services" className="py-20 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-accent-bg text-reliable-sky border-none mb-4">Our Services</Badge>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-4">
            Comprehensive Plumbing Services
          </h2>
          <p className="text-lg text-slate-600">
            From emergency repairs to bathroom renovations, Richard provides reliable
            plumbing solutions for homes and businesses across the Inner West.
          </p>
        </div>

        {/* Services Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service) => (
            <Card
              key={service.id}
              id={`service-${service.slug}`}
              className={`service-card border-slate-100 ${
                service.featured ? "lg:col-span-1 bg-white" : "bg-white"
              } ${highlighted === service.slug ? "highlighted" : ""}`}
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-8">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 service-icon ${
                  service.featured ? "bg-reliable-sky text-white" : "bg-accent-bg text-reliable-sky"
                }`}>
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-trust-navy mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">Need help with something else?</p>
          <a 
            href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-2 text-reliable-sky font-semibold hover:text-reliable-sky-dark transition-colors"
            data-testid="services-call-link"
          >
            <Phone className="w-5 h-5" />
            Give Richard a call to discuss your needs
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  return (
    <section id="why-us" className="py-20 md:py-32 bg-accent-bg" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <Badge className="bg-white text-reliable-sky border-none mb-4">Why Choose Us</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-6">
              Why Inner West Families Trust Richard Knight Plumbing
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              For over two decades, Richard has built a reputation on honest advice, 
              quality workmanship, and genuine care for his customers. Here is what 
              sets us apart.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_CHOOSE.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <item.icon className="w-6 h-6 text-reliable-sky" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-trust-navy mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img 
                // src="https://images.unsplash.com/photo-1713527555117-85e0129fd90c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwbHVtYmluZyUyMHRvb2xzJTIwd3JlbmNoJTIwcGlwZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzAzNDE3MTl8MA&ixlib=rb-4.1.0&q=85"
                src="/pipes.png"
                alt="Professional plumbing tools"
                className="w-full h-full object-cover"
                data-testid="why-us-image"
              />
            </div>
            
            {/* Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="font-heading font-bold text-3xl text-trust-navy">68</p>
                  <p className="text-xs text-slate-500">5-Star Reviews</p>
                </div>
                <Separator orientation="vertical" className="h-12" />
                <div className="text-center">
                  <p className="font-heading font-bold text-3xl text-trust-navy">20+</p>
                  <p className="text-xs text-slate-500">Years Local</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const trackRef = useRef(null);
  const isHovering = useRef(false);
  const animFrameRef = useRef(null);
  const duplicatedTestimonials = [...TESTIMONIALS, ...TESTIMONIALS];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = () => {
      if (!isHovering.current) {
        track.scrollLeft += 0.5;
        // Seamlessly loop when first half is consumed
        if (track.scrollLeft >= track.scrollWidth / 2) {
          track.scrollLeft = track.scrollLeft - track.scrollWidth / 2;
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  return (
    <section id="testimonials" className="py-20 md:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-accent-bg text-reliable-sky border-none mb-4">Testimonials</Badge>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-slate-600">
            Do not take our word for it. Here is what Inner West residents
            have to say about their experience with Richard Knight Plumbing.
          </p>
        </div>
      </div>

      {/* Infinite Scroll Testimonials */}
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto pt-3 pb-4 testimonials-track"
        onMouseEnter={() => { isHovering.current = true; }}
        onMouseLeave={() => { isHovering.current = false; }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div
            key={`${testimonial.id}-${index}`}
            className="flex-shrink-0 w-[350px] md:w-[400px] first:ml-4 last:mr-4"
          >
            <Card className="testimonial-card bg-muted-bg border-l-4 border-reliable-sky h-full" data-testid={`testimonial-${testimonial.id}`}>
              <CardContent className="p-8">
                <Quote className="w-10 h-10 text-reliable-sky/20 mb-4" />
                <p className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-reliable-sky/10 rounded-full flex items-center justify-center">
                    <span className="text-reliable-sky font-semibold text-sm">
                      {testimonial.source.charAt(0)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-600">{testimonial.source}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Google Rating CTA */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-6 py-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-slate-600 font-medium">5.0 rating from 68 Google reviews</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 md:py-32 bg-trust-navy text-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden">
              <img 
                // src="https://images.unsplash.com/photo-1765766556463-180500e61ea8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMGJhdGhyb29tJTIwaW50ZXJpb3IlMjB3aGl0ZXxlbnwwfHx8fDE3NzAzNDE3MTR8MA&ixlib=rb-4.1.0&q=85"
                // src="/water-service-bf-af.png"
                src="/car-v2.png"
                alt="Modern clean bathroom"
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <Badge className="bg-white/10 text-white border-none mb-4">About Richard</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
              Your Local Inner West Plumber
            </h2>
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>
                Richard Knight has been providing quality plumbing services to 
                Newtown and the Inner West for over 20 years. As an independent 
                local plumber, he takes pride in building lasting relationships 
                with customers through honest advice and reliable service.
              </p>
              <p>
                Whether it is a dripping tap or a complete bathroom renovation, 
                Richard brings the same level of care and professionalism to every 
                job. He believes in doing the work right the first time, communicating 
                clearly about costs and options, and leaving your home as tidy as 
                he found it.
              </p>
              <p>
                Many customers have trusted Richard with their plumbing needs for 
                over a decade. That kind of loyalty comes from consistently delivering 
                quality workmanship and treating every customer with respect.
              </p>
            </div>

            <div className="mt-8">
              <a 
                href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-2 bg-white text-trust-navy rounded-full px-8 py-4 font-semibold hover:bg-slate-100 transition-colors"
                data-testid="about-call-btn"
              >
                <Phone className="w-5 h-5" />
                Have a chat with Richard
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Area Section
const ServiceAreaSection = () => {
  return (
    <section id="service-area" className="py-20 md:py-32" data-testid="service-area-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <Badge className="bg-accent-bg text-reliable-sky border-none mb-4">Service Area</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-6">
              Proudly Serving the Inner West
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Richard Knight Plumbing is based in Newtown and provides prompt, 
              reliable service throughout Sydney's Inner West suburbs.
            </p>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {SERVICE_AREAS.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-slate-600"
                >
                  <MapPin className="w-4 h-4 text-reliable-sky flex-shrink-0" />
                  <span className="text-sm">{area}</span>
                </div>
              ))}
            </div>

            <p className="text-slate-500 text-sm mt-6">
              Not sure if we service your area? Give us a call and we will let you know.
            </p>
          </div>

          {/* Map/Image */}
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <img 
              src="https://images.unsplash.com/photo-1760129745103-91c4022ed5fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxzeWRuZXklMjBhdXN0cmFsaWElMjBzdWJ1cmJhbiUyMHN0cmVldCUyMHN1bm55fGVufDB8fHx8MTc3MDM0MTcyNHww&ixlib=rb-4.1.0&q=85"
              alt="Sydney suburban street"
              className="w-full h-full object-cover"
              data-testid="service-area-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 md:py-32 bg-accent-bg" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 mb-6">
          <Phone className="w-4 h-4 text-reliable-sky" />
          <span className="text-sm font-medium text-trust-navy">Quick Response Guaranteed</span>
        </div>
        
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-6">
          Need a Reliable Plumber You Can Trust?
        </h2>
        
        <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
          Whether it is an emergency or you are planning ahead, Richard is here to help. 
          Call for a chat about your plumbing needs - no pressure, just honest advice.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center justify-center gap-2 bg-reliable-sky hover:bg-reliable-sky-dark text-white rounded-full px-10 py-5 text-lg font-semibold shadow-lg btn-primary"
            data-testid="cta-call-btn"
          >
            <Phone className="w-5 h-5" />
            Call {BUSINESS.phone}
          </a>
          <a 
            href={`mailto:${BUSINESS.email}`}
            className="inline-flex items-center justify-center gap-2 bg-white text-trust-navy border-2 border-trust-navy rounded-full px-10 py-5 text-lg font-semibold btn-secondary"
            data-testid="cta-email-btn"
          >
            <Mail className="w-5 h-5" />
            Send an Email
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-20 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <Badge className="bg-accent-bg text-reliable-sky border-none mb-4">Get In Touch</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-trust-navy tracking-tight mb-6">
              Contact Richard Knight Plumbing
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Ready to book a job or have questions? Get in touch and Richard will 
              get back to you promptly.
            </p>

            <div className="space-y-6">
              {/* Phone */}
              <a 
                href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-4 p-4 bg-muted-bg rounded-2xl hover:bg-accent-bg transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-reliable-sky rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Call or Text</p>
                  <p className="font-heading font-bold text-xl text-trust-navy">{BUSINESS.phone}</p>
                </div>
              </a>

              {/* Email */}
              <a 
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-4 p-4 bg-muted-bg rounded-2xl hover:bg-accent-bg transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-reliable-sky rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Email</p>
                  <p className="font-heading font-semibold text-trust-navy break-all">{BUSINESS.email}</p>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-4 p-4 bg-muted-bg rounded-2xl">
                <div className="w-14 h-14 bg-reliable-sky rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Based In</p>
                  <p className="font-heading font-semibold text-trust-navy">{BUSINESS.location}</p>
                </div>
              </div>
            </div>

            {/* Emergency Note */}
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 mb-1">Plumbing Emergency?</p>
                  <p className="text-sm text-amber-700">
                    Call Richard directly for urgent plumbing issues. Same-day response 
                    for emergencies in the Inner West.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Business Hours Card */}
          <div className="lg:pt-20">
            <Card className="bg-trust-navy text-white">
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl font-bold mb-6">Business Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-300">Monday - Friday</span>
                    <span className="font-medium">7:00am - 5:00pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Saturday</span>
                    <span className="font-medium">Closed</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Sunday</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
                <Separator className="my-6 bg-white/20" />
                <p className="text-sm text-slate-300">
                  Available for emergency call-outs outside regular hours. 
                  Call to discuss urgent plumbing needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-trust-navy text-white pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <img 
                // src="https://customer-assets.emergentagent.com/job_3d9d93ab-e25a-4850-bb7a-ddafb0558447/artifacts/akpu30p9_ChatGPT%20Image%20Feb%206%2C%202026%2C%2002_03_43%20PM.png"
                src="/logo.png"
                alt="Richard Knight Plumbing"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-slate-400 max-w-md leading-relaxed">
              Your trusted local plumber in Newtown and the Inner West. 
              Over 20 years of reliable service, honest advice, and quality workmanship.
            </p>
            <div className="flex items-center gap-2 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-sm text-slate-400 ml-2">5.0 from 68 reviews</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#service-blocked-drains" className="hover:text-white transition-colors">Blocked Drains</a></li>
              <li><a href="#service-hot-water-systems" className="hover:text-white transition-colors">Hot Water Systems</a></li>
              <li><a href="#service-gas-fitting" className="hover:text-white transition-colors">Gas Fitting</a></li>
              <li><a href="#service-emergency-plumbing" className="hover:text-white transition-colors">Emergency Plumbing</a></li>
              <li><a href="#service-bathroom-renovations" className="hover:text-white transition-colors">Bathroom Renovations</a></li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Service Areas</h4>
            <ul className="space-y-2 text-slate-400">
              <li>Newtown</li>
              <li>Marrickville</li>
              <li>Enmore</li>
              <li>Stanmore</li>
              <li>Petersham</li>
              <li>Inner West Sydney</li>
            </ul>
          </div>
        </div>

        <Separator className="bg-white/10 mb-8" />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-400">
            {currentYear} Richard Knight Plumbing. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a 
              href={`tel:${BUSINESS.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">{BUSINESS.phone}</span>
            </a>
            <a 
              href={`mailto:${BUSINESS.email}`}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Email Us</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App font-body">
      <Header />
      <main>
        <HeroSection />
        <TrustSignals />
        <ServicesSection />
        <WhyChooseSection />
        <TestimonialsSection />
        <AboutSection />
        <ServiceAreaSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
